import { Grammar } from './grammar.js';
import type { TranslationLabels } from '../types/labels.type.js';
import { Language, Locale } from './enums.js';

// Make sure the storage is global
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any;
window['UI-LABELS-STORAGE'] ??= {};
export const labelsRegistry: Record<string, TranslationLabels.Labels> =
    window['UI-LABELS-STORAGE'];

class LabelsRegistry {
    LOCALE: Locale = Locale.LegacyEN;
    LANGUAGE: Language = Language.English;

    /**
     * @param {string} target
     * @returns {Record<string, string>}
     */
    getAll(target: string): TranslationLabels.Labels {
        labelsRegistry[target] ??= {};
        return labelsRegistry[target];
    }

    /**
     * @param {string} target
     * @param {Labels} [defaultLabels]
     * @returns {TranslationLabels.Labels}
     */
    attach<T extends TranslationLabels.Labels>(
        target: string,
        defaultLabels: T
    ): T {
        const data = this.getAll(target);
        if (defaultLabels) {
            Object.keys(defaultLabels).forEach((key) => {
                if (!data.hasOwnProperty(key)) {
                    data[key] = defaultLabels[key];
                }
            });
        }
        return data as T;
    }

    /**
     * @param {string} target
     * @param {string | object} obj
     * @param {string} [label]
     */
    set(
        target: string,
        obj: TranslationLabels.Label,
        label: TranslationLabels.Label
    ): void {
        const data = this.getAll(target);
        if (typeof obj === 'string') {
            data[obj] = label;
        } else if (typeof obj === 'object') {
            Object.entries(obj).forEach(([key, value]) => {
                data[key] = value;
            });
        }
    }

    /**
     * @param {Labels} labelStorage
     * @param {string} key
     * @param {Record<string, unknown> | number | string} substitutions
     * @returns {Label}
     */
    processLabel(
        labelStorage: TranslationLabels.Labels,
        key: string,
        substitutions: TranslationLabels.Substitutions
    ): TranslationLabels.Label {
        const type = typeof substitutions;
        let quantityDefined;
        if (!labelStorage) {
            return key;
        }
        const label = labelStorage.hasOwnProperty(key)
            ? labelStorage[key]
            : key;

        switch (type) {
            case 'number':
                quantityDefined = typeof substitutions === 'number';
                if (quantityDefined) {
                    const quantifiedKey =
                        key + '.' + Grammar.quantify(substitutions as number);
                    const rawLabel = labelStorage[quantifiedKey];
                    if (typeof rawLabel === 'string') {
                        return rawLabel.replace(
                            /%q/g,
                            String(substitutions as number)
                        );
                    }
                    const singleLabel = labelStorage[key];
                    return typeof singleLabel === 'string'
                        ? singleLabel.replace(
                              /%q/g,
                              String(substitutions as number)
                          )
                        : quantifiedKey;
                }
                return label;
            case 'object':
                if (Array.isArray(substitutions)) {
                    return substitutions.reduce<string>(
                        (str: string, entry: string, index: number) =>
                            str.replace('%' + (index + 1), entry),
                        label as string
                    );
                } else {
                    return typeof label === 'string'
                        ? Object.entries(substitutions || {}).reduce(
                              (str, [key, value]) =>
                                  str.replace(`{${key}}`, String(value)),
                              label
                          )
                        : label;
                }
            default:
                return label;
        }
    }
}

export const Labels = new LabelsRegistry();
